// Чистая модель разбора: PGN партии → главная линия узлов с классификацией,
// счётчики ошибок, accuracy, win%-граф. Без DOM — покрыта юнит-тестами.
// Движковый (WASM) путь для партий без [%eval] придёт в Phase 3.
import { Chess } from 'chess.js';
import { loadGameFromPgn } from '@ext/core/game-load';
import { EvalStore } from '@ext/core/evals';
import { mainLineOf } from '@ext/core/tree';
import { classifyMove, type MoveQuality, type ClassifyCtx } from '@ext/core/classify';
import { gameStats, collectKeyMoments, type KeyMoment } from '@ext/core/accuracy';
import { sideForNick, pgnTag, type CoachGame } from '@ext/core/game-sources';
import type { GameNode } from '@ext/core/types';
import { applyLooseMove } from '@ext/core/move-detect';

export interface MoveVM {
  node: GameNode;
  /** 1-based индекс полухода в главной линии. */
  ply: number;
  /** Номер хода для записи (fullmove). */
  moveNo: number;
  /** Ходил белый. */
  white: boolean;
  san: string;
  quality: MoveQuality | null;
  /** cp со стороны белых после хода (для eval bar/карточки). */
  evalAfterCp: number | null;
  from: string;
  to: string;
  /** Полный FEN после хода. */
  fen: string;
}

export interface ReviewCounts {
  blunder: number;
  mistake: number;
  inaccuracy: number;
}

export interface ReviewModel {
  /** Есть засеянные [%eval] → маркеры/бар/граф доступны без движка. */
  engineReady: boolean;
  side: 'w' | 'b' | null;
  orientation: 'white' | 'black';
  white: string;
  black: string;
  whiteElo: string;
  blackElo: string;
  result: string;
  speed: string;
  /** Accuracy% стороны пользователя (или null). */
  accuracy: number | null;
  counts: ReviewCounts;
  moves: MoveVM[];
  rootFen: string;
  /** Корень дерева партии — для движкового разбора (reviewGame/on-demand). */
  root: GameNode;
  /** win% по полуходам (включая стартовую позицию). */
  graph: number[];
  moments: KeyMoment[];
  evals: EvalStore;
}

function squaresOf(parentFen: string, san: string): { from: string; to: string } {
  try {
    const c = new Chess();
    c.load(parentFen, { skipValidation: true });
    const m = applyLooseMove(c, san);
    if (m) return { from: m.from, to: m.to };
  } catch {
    /* нелегальный/непонятный SAN — вернём пустые клетки */
  }
  return { from: '', to: '' };
}

function mergeCounts(a: Partial<Record<MoveQuality, number>>, b: Partial<Record<MoveQuality, number>>): ReviewCounts {
  const g = (k: MoveQuality) => (a[k] || 0) + (b[k] || 0);
  return { blunder: g('blunder') + g('miss'), mistake: g('mistake'), inaccuracy: g('inaccuracy') };
}

/** Построить модель разбора. Бросает при неразборном PGN (ловит вызывающий). */
export function buildReview(game: CoachGame, nick: string): ReviewModel {
  const evals = new EvalStore();
  const loaded = loadGameFromPgn(game.pgn, null, evals);
  const root = loaded.game.root;
  const engineReady = loaded.seededEvals > 0;
  const ctx: ClassifyCtx = { evals, depth: 0 };

  const line = mainLineOf(root);
  const moves: MoveVM[] = line.map((node, i) => {
    const sq = squaresOf(node.parent!.fen, node.san || '');
    return {
      node,
      ply: i + 1,
      moveNo: node.moveNo ?? Math.floor(i / 2) + 1,
      white: node.moverWhite === true,
      san: node.san || '',
      quality: engineReady ? classifyMove(node, ctx) : null,
      evalAfterCp: engineReady ? evals.getNode(node) : null,
      from: sq.from,
      to: sq.to,
      fen: node.fen,
    };
  });

  const side = sideForNick(game, { nick });
  const stats = gameStats(root, ctx);
  const mySide = side ? stats[side] : null;
  const counts = mySide
    ? mergeCounts(mySide.counts, {})
    : mergeCounts(stats.w.counts, stats.b.counts);

  return {
    engineReady,
    side,
    orientation: side === 'b' ? 'black' : 'white',
    white: game.white || pgnTag(game.pgn, 'White') || '—',
    black: game.black || pgnTag(game.pgn, 'Black') || '—',
    whiteElo: pgnTag(game.pgn, 'WhiteElo'),
    blackElo: pgnTag(game.pgn, 'BlackElo'),
    result: game.result || pgnTag(game.pgn, 'Result') || '',
    speed: game.speed || '',
    accuracy: mySide ? mySide.accuracy : null,
    counts,
    moves,
    rootFen: loaded.game.startFen,
    root,
    graph: engineReady ? stats.graph : [],
    moments: engineReady ? collectKeyMoments(root, ctx) : [],
    evals,
  };
}

/**
 * Пересчитать движковые поля модели из текущего состояния evals/узлов —
 * вызывается по мере прогресса WASM-разбора (прогрессивный рендер) и после
 * on-demand анализа. Мутирует модель на месте: качество/оценки ходов, счётчики,
 * accuracy, win%-граф, ключевые моменты и флаг engineReady.
 */
export function refreshEngineData(model: ReviewModel): void {
  const ctx: ClassifyCtx = { evals: model.evals, depth: 0 };
  for (const mv of model.moves) {
    mv.quality = classifyMove(mv.node, ctx);
    mv.evalAfterCp = model.evals.getNode(mv.node);
  }
  const stats = gameStats(model.root, ctx);
  const side = model.side;
  const mySide = side ? stats[side] : null;
  model.counts = mySide ? mergeCounts(mySide.counts, {}) : mergeCounts(stats.w.counts, stats.b.counts);
  model.accuracy = mySide ? mySide.accuracy : null;
  model.graph = stats.graph;
  model.moments = collectKeyMoments(model.root, ctx);
  model.engineReady = model.evals.size > 0;
}
